import { useEffect, useRef, useState } from "react";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import Box from "@amzn/awsui-components-console/box";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import AttributeEditor, { AttributeEditorProps } from "@amzn/awsui-components-react/polaris/attribute-editor";
import Input from "@amzn/awsui-components-react/polaris/input";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import ButtonDropdown, { ButtonDropdownProps } from "@amzn/awsui-components-react/polaris/button-dropdown";

import { QuickActionItem } from "../../../services/chatbot-service";
import { getString } from "../../../common/ui-string-labels/ui-strings-utils";

const MAX_ITEMS = 10;
const TITLE_MAX_LENGTH = 50;
const PROMPT_MAX_LENGTH = 1000;

interface QuickActionsModalProps {
    visible: boolean;
    onDismiss: () => void;
    items: QuickActionItem[];
    onSave: (items: QuickActionItem[]) => Promise<boolean>;
}

interface EditableItem {
    id?: string;
    title: string;
    prompt: string;
}

interface ItemErrors {
    title?: string;
    prompt?: string;
}

const normalizeTitle = (title: string) => title.trim().toLowerCase();

const validateItem = (item: EditableItem, index: number, items: EditableItem[]): ItemErrors => {
    const errors: ItemErrors = {};
    const trimmedTitle = item.title.trim();
    if (!trimmedTitle) {
        errors.title = getString('chatbot.quickActions.titleRequired');
    } else if (item.title.length > TITLE_MAX_LENGTH) {
        errors.title = getString('chatbot.quickActions.titleTooLong');
    } else if (items.some((other, i) => i !== index && normalizeTitle(other.title) === normalizeTitle(item.title))) {
        errors.title = getString('chatbot.quickActions.titleDuplicate');
    }
    const trimmedPrompt = item.prompt.trim();
    if (!trimmedPrompt) {
        errors.prompt = getString('chatbot.quickActions.promptRequired');
    } else if (item.prompt.length > PROMPT_MAX_LENGTH) {
        errors.prompt = getString('chatbot.quickActions.promptTooLong');
    }
    return errors;
};

type ReorderAction = 'move-up' | 'move-down' | 'remove';

const updateItemPosition = (items: EditableItem[], index: number, action: ReorderAction): EditableItem[] => {
    const updated = [...items];
    if (action === 'move-up') {
        [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    } else if (action === 'move-down') {
        [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    } else {
        updated.splice(index, 1);
    }
    return updated;
};

const QuickActionsModal = ({ visible, onDismiss, items, onSave }: QuickActionsModalProps) => {
    const [localItems, setLocalItems] = useState<EditableItem[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);

    const prevVisible = useRef(false);
    useEffect(() => {
        if (visible && !prevVisible.current) {
            setLocalItems(items.map((item) => ({ id: item.id, title: item.title, prompt: item.prompt })));
            setSubmitted(false);
            setSaveError(false);
        }
        prevVisible.current = visible;
    }, [visible, items]);

    const getTitleError = (item: EditableItem, index: number): string | undefined =>
        submitted ? validateItem(item, index, localItems).title : undefined;

    const getPromptError = (item: EditableItem, index: number): string | undefined =>
        submitted ? validateItem(item, index, localItems).prompt : undefined;

    const handleSave = async () => {
        setSubmitted(true);
        setSaveError(false);

        const hasErrors = localItems.some((item, index) => {
            const errors = validateItem(item, index, localItems);
            return !!(errors.title || errors.prompt);
        });

        if (hasErrors) return;

        setSaving(true);
        const success = await onSave(localItems as QuickActionItem[]);
        setSaving(false);

        if (success) {
            onDismiss();
        } else {
            setSaveError(true);
        }
    };

    const definition: AttributeEditorProps.FieldDefinition<EditableItem>[] = [
        {
            label: getString('chatbot.quickActions.titleLabel'),
            control: (item: EditableItem, itemIndex: number) => (
                <Input
                    value={item.title}
                    placeholder={getString('chatbot.quickActions.titlePlaceholder')}
                    onChange={({ detail }) => {
                        if (detail.value.length > TITLE_MAX_LENGTH) return;
                        const updated = [...localItems];
                        updated[itemIndex] = { ...updated[itemIndex], title: detail.value };
                        setLocalItems(updated);
                    }}
                />
            ),
            constraintText: (item: EditableItem) => `${item.title.length}/${TITLE_MAX_LENGTH}`,
            errorText: (item: EditableItem, itemIndex: number) => getTitleError(item, itemIndex),
        },
        {
            label: getString('chatbot.quickActions.promptLabel'),
            control: (item: EditableItem, itemIndex: number) => (
                <Textarea
                    value={item.prompt}
                    placeholder={getString('chatbot.quickActions.promptPlaceholder')}
                    rows={2}
                    onChange={({ detail }) => {
                        if (detail.value.length > PROMPT_MAX_LENGTH) return;
                        const updated = [...localItems];
                        updated[itemIndex] = { ...updated[itemIndex], prompt: detail.value };
                        setLocalItems(updated);
                    }}
                />
            ),
            constraintText: (item: EditableItem) => `${item.prompt.length}/${PROMPT_MAX_LENGTH}`,
            errorText: (item: EditableItem, itemIndex: number) => getPromptError(item, itemIndex),
        },
    ];

    return (
        <Modal
            data-testid="chatbot-quick-actions-modal"
            visible={visible}
            onDismiss={onDismiss}
            header={getString('chatbot.quickActions.modalHeader')}
            size="large"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={onDismiss}>
                            {getString('common.cancel')}
                        </Button>
                        <Button variant="primary" onClick={handleSave} loading={saving}>
                            {getString('chatbot.quickActions.saveButton')}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <SpaceBetween size="m">
                {saveError && (
                    <Alert type="error">
                        {getString('chatbot.quickActions.saveError')}
                    </Alert>
                )}
                <AttributeEditor
                    onAddButtonClick={() => setLocalItems([...localItems, { title: '', prompt: '' }])}
                    items={localItems}
                    addButtonText={getString('chatbot.quickActions.addButton')}
                    empty={getString('chatbot.quickActions.empty')}
                    additionalInfo={
                        localItems.length >= MAX_ITEMS
                            ? getString('chatbot.quickActions.limitReached')
                            : getString('chatbot.quickActions.remaining', { count: MAX_ITEMS - localItems.length })
                    }
                    disableAddButton={localItems.length >= MAX_ITEMS}
                    definition={definition}
                    gridLayout={[
                        { rows: [[1, 2]], removeButton: { ownRow: false, width: 'auto' } },
                    ]}
                    customRowActions={({ itemIndex, ref }) => (
                        <ButtonDropdown
                            ref={ref as React.Ref<ButtonDropdownProps.Ref>}
                            variant="icon"
                            items={[
                                { id: "move-up", text: getString('chatbot.quickActions.moveUp'), disabled: itemIndex === 0 },
                                { id: "move-down", text: getString('chatbot.quickActions.moveDown'), disabled: itemIndex === localItems.length - 1 },
                                { id: "remove", text: getString('chatbot.quickActions.removeButton') },
                            ]}
                            onItemClick={({ detail }) => {
                                setLocalItems((items) => updateItemPosition(items, itemIndex, detail.id as ReorderAction));
                            }}
                            ariaLabel={getString('chatbot.quickActions.rowActionsAriaLabel')}
                        />
                    )}
                />
            </SpaceBetween>
            </div>
        </Modal>
    );
};

export default QuickActionsModal;
